import { createReducer, createSelector } from 'redux-starter-kit';
import { putInspectionReason, putInspectionReasons } from './inspectionReasonActions';


/**
 * A selector for the reasons loaded in the domain state.
 * This is a dict object: { inspectionReasonCode: inspectionReason }
 * @param {object} state The redux state of the application.
 * @returns A keyed object mapping inspectionReasonCode to damage area objects.
 * @example const inspectionReason = selectInspectionReasonsMap(state)[yourInspectionReasonCode];
 */
export const selectInspectionReasonsMap = state => state.via.domain.inspectionReasons;

/**
 * A selector for the reasons loaded in the domain state.
 * This is a list: [ inspectionReason, inspectionReason, ...]
 * @param {object} state The redux state of the application.
 * @returns An array of damage area objects.
 * @example const inspectionReasons = selectInspectionReasons(state);
 */
export const selectInspectionReasons = createSelector(['via.domain.inspectionReasons'], map => (map ? Object.values(map) : null));

/**
 * A selector for a single reason.
 * @param {object} state The redux state of the application.
 * @param {string} inspectionReasonCode The id of the damage area.
 * @returns The damage area object or null if it doesn't exist.
 * @example const inspectionReason = selectInspectionReason(state, inspectionReasonCode);
 */
export const selectInspectionReason = (state, inspectionReasonCode) => {
  const inspectionReasonMap = selectInspectionReasonsMap(state);
  if (!inspectionReasonMap) return null;

  return inspectionReasonMap[inspectionReasonCode];
};


/**
 * A dict object with a property key of: inspectionReasonCode and a property value of:
 * inspectionReason.
 * @example { code1: { inspectionReasonCode: 'code1', ... },
 * code2: { inspectionReasonCode: 'code2', ... } }
 */
const initialInspectionReasons = {
  // inspectionReasonCode: { /* damage area object */ }
  // inspectionReasonCode: { /* damage area object */ }
  // ...etc
};

/**
 * A reducer for managing the Damage Areas in the domain.
 */
const inspectionReasonReducer = createReducer(initialInspectionReasons, {
  // Inserts/Updates the inspection reason for the id.
  [putInspectionReason]: (state, action) => {
    const inspectionReason = action.payload;

    return {
      ...state,
      [inspectionReason.reasonCode]: inspectionReason,
    };
  },

  // Inserts/Updates all inspection reasons.
  [putInspectionReasons]: (state, action) => {
    const inspectionReasons = action.payload;
    // Builds a dict object. inspectionReasonCode => inspectionReason
    return inspectionReasons.reduce((newState, reason) => {
      newState[reason.reasonCode] = reason; // eslint-disable-line no-param-reassign
      return newState;
    }, { ...initialInspectionReasons });
  },
});

export default inspectionReasonReducer;
